import * as THREE from 'three';
import { IAREngine } from './ar-engine.interface';
import { AREngineType, AREngineConfig } from './ar-types';
import { MindAREngine } from './mindar/mindar-engine';

/**
 * Factory for creating AR engine instances
 */
export class AREngineFactory {
  /**
   * Create an AR engine based on configuration
   * @param config AR engine configuration
   * @param scene Three.js scene (used by some engines)
   * @param renderer Three.js renderer (used by some engines)
   * @returns AR engine instance
   */
  static create(
    config: AREngineConfig,
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer
  ): IAREngine {
    // For now only MindAR is supported
    console.log('🧠 Creating MindAR engine');
    return new MindAREngine(scene, renderer);
  }

  /**
   * Create an AR engine based on engine type string
   * Convenience method for simple engine selection
   * @param engineType Engine type as string ('zappar' or 'mindar')
   * @param scene Three.js scene
   * @param renderer Three.js renderer
   * @param additionalConfig Optional additional configuration
   * @returns AR engine instance
   */
  static createFromType(
    engineType: string,
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer,
    additionalConfig?: Partial<AREngineConfig>
  ): IAREngine {
    const config: AREngineConfig = {
      engineType: AREngineType.MINDAR,
      ...additionalConfig
    };

    return AREngineFactory.create(config, scene, renderer);
  }

  /**
   * Get the default AR engine type
   * Can be overridden by environment variables or configuration
   */
  static getDefaultEngineType(): AREngineType {
    // Default to MindAR going forward
    return AREngineType.MINDAR;
  }

  /**
   * List all available AR engine types
   */
  static getAvailableEngines(): Array<{ type: AREngineType; name: string; description: string }> {
    return [
      {
        type: AREngineType.MINDAR,
        name: 'MindAR',
        description: 'Open-source web AR engine with TensorFlow.js (.mind format)'
      }
    ];
  }
}
